import { FC, memo, useMemo } from "react";
import styled from "styled-components";
import orderBy from "lodash.orderby";

import {
  SortableTh,
  Table,
  TableContent,
  TableHeader,
  Th,
} from "@/components/Table";
import { SectionHeading, SrOnly } from "@/components/Layout/Parts";
import OptimisationTableRow from "./OptimisationTableRow";
import { OptimisationProjectResource } from "@/types/resources";
import { OptimisationProjectStatusEnum, SortDirection } from "@/utils/enums";
import ScrollableTable from "@/components/ScrollableTable";
import useSort from "@/hooks/useSort";

export const StyledSectionHeading = styled(SectionHeading)`
  margin-top: 32px;
  margin-bottom: 0;
`;

export const StyledTable = styled(Table)`
  th,
  td {
    padding: 16px 14px;
    font-size: 11px;
    vertical-align: top;
    &:nth-child(1) {
      width: 33%;
      padding-left: 24px;
    }
    &:nth-child(2) {
      width: 16%;
    }
    &:nth-child(3) {
      width: 16%;
    }
    &:nth-child(4) {
      width: 16%;
    }
    &:nth-child(5) {
      width: 16%;
    }
    &:last-child {
      width: 60px;
      padding-right: 24px;
      position: relative;
    }
  }
`;

interface Props {
  status: OptimisationProjectStatusEnum;
  projects?: OptimisationProjectResource[];
  isLoading: boolean;
  isError: boolean;
}

const OptimisationTable: FC<Props> = memo(
  ({ status, projects, isLoading, isError }) => {
    const renderCurrentStatus = () => {
      switch (status) {
        case OptimisationProjectStatusEnum.DRAFT:
          return "drafts";
        case OptimisationProjectStatusEnum.RUNNING:
          return "running";
        case OptimisationProjectStatusEnum.ENDED:
          return "ended";
        default:
          return null;
      }
    };

    const [sort, setSort] = useSort({
      direction: SortDirection.DEFAULT,
      property: "projectId",
    });

    const sortedProjects = useMemo(() => {
      if (sort.direction === SortDirection.DEFAULT) {
        return projects;
      }

      const property = sort.property as keyof OptimisationProjectResource;

      const direction = sort.direction.toLowerCase() as "asc" | "desc";

      return orderBy(projects, (project) => project[property], direction);
    }, [projects, sort]);

    return (
      <>
        <TableHeader>
          <StyledSectionHeading>{renderCurrentStatus()}</StyledSectionHeading>
        </TableHeader>
        <ScrollableTable>
          <StyledTable $fixed>
            <thead>
              <tr>
                <SortableTh
                  sort={sort}
                  setSort={setSort}
                  title="Project Name"
                  subtitle="Project ID"
                  property="projectId"
                />
                <Th>Thumbs</Th>
                <Th>Tips</Th>
                <Th>Sequences</Th>
                <Th>Intros</Th>
                <Th $actionsCol>
                  <SrOnly>Actions</SrOnly>
                </Th>
              </tr>
            </thead>
            <tbody>
              <TableContent
                isLoading={isLoading}
                isError={isError}
                hasData={!!sortedProjects?.length}
              >
                {sortedProjects?.map((project) => (
                  <OptimisationTableRow project={project} key={project.id} />
                ))}
              </TableContent>
            </tbody>
          </StyledTable>
        </ScrollableTable>
      </>
    );
  }
);

export default OptimisationTable;
